import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Money } from '../../Money';
import { getLocale } from '../../utils';
import PriceParts from './PriceParts/PriceParts';
import { useTheme } from '../../theme';
import GooglePay from './Gateway/GooglePay/GooglePay';
import Kaspi from './Gateway/Kaspi/Kaspi';
import Iframe from './Gateway/Iframe/Iframe';
import { CardPaymentMethodWithUrl, PaymentPage, PaymentType } from '../types';
import Link from '../../Link';
import Test from './Gateway/Test/Test';
import MediaQuery from '../../MediaQuery/MediaQuery';
import { arrowIcon, emptyCard } from '../Icons';
import { BaseButton } from '../../BaseComponents';
import Notification from '../../Notification/components/Notification';
import { CheckoutState } from '../../Checkout/store/reducers';
import {
	CardPaymentMethodFragment,
	CashMethodFragment,
	CheckoutOrderFragment,
	CompletePaymentMutationVariables,
	GatewayPaymentFragment,
	GooglePayMethodFragment,
	InfoMessageTypeEnum,
	InvoiceMethodFragment,
	OrderPriceFragment,
	PaymentProvider
} from '@websky/graphql';
import Segments from './Segments/Segments';
import SummaryGroup from './Summary/SummaryGroup';
import Summary from './Summary/Summary';
import SummaryItem from './Summary/SummaryItem';
import PaymentTimeLimit from '../../PaymentTimeLimit';
import Cash from './Gateway/Cash/Cash';
import Invoice from './Gateway/Invoice/Invoice';
import Qiwi from './Gateway/Qiwi/Qiwi';
import EmptyWrapper from './EmptyWrapper/EmptyWrapper';
import { PaymentResult } from '@entities/payment';
import { useConfig } from '../../context';
import { useRenderers } from '../../renderProps';
import { Currency } from '../../enums';
import Tooltip from '../../BaseComponents/Tooltip';
import { useSentryTranslation } from '../../hooks';
import PromoLoader from '../../PromoLoader';
import SimpleLoader from '../../SimpleLoader';
import { SafeHtml } from '@shared/ui';
import { getSinglePaymentPage, isPaymentMethodReadyToOpen } from '../utils';

interface Props {
	paymentPage?: PaymentPage;
	onPaymentMethodSelected?: React.Dispatch<PaymentPage>;
	onPaymentFrameLoaded?: () => void;
	priceToPay: CheckoutState['orderPrice']['priceToPay'];
	onSuccess?: () => void;
	onFail?: () => void;
	credit?: CardPaymentMethodWithUrl;
	card?: CardPaymentMethodWithUrl;
	itransfer?: CardPaymentMethodWithUrl;
	gpay?: GooglePayMethodFragment;
	kaspi?: GatewayPaymentFragment;
	cash?: CashMethodFragment;
	invoice?: InvoiceMethodFragment;
	qiwi?: GatewayPaymentFragment;
	sbol?: CardPaymentMethodWithUrl;
	fps?: CardPaymentMethodWithUrl;
	podeli?: CardPaymentMethodWithUrl;
	ffpBonus?: CardPaymentMethodWithUrl;
	uni_qr?: CardPaymentMethodWithUrl;
	onTestPayment?: () => void;
	onBackToOrder?: () => void;
	order?: CheckoutOrderFragment;
	onCompletePayment?: (values: Omit<CompletePaymentMutationVariables, 'orderId'>) => Promise<boolean>;
	isPaymentAvailable?: boolean;
	isPaymentPending?: boolean;
	isAwaitingGatewayPayload?: boolean;
	orderIsRefetching?: boolean;
	openFrameInNewTab?: boolean;
}

const PaymentForm: React.FC<Props> = ({
	card,
	onPaymentFrameLoaded,
	credit,
	gpay,
	itransfer,
	kaspi,
	cash,
	fps,
	sbol,
	invoice,
	qiwi,
	podeli,
	ffpBonus,
	uni_qr,
	priceToPay,
	onSuccess,
	onFail,
	onTestPayment,
	onBackToOrder,
	onPaymentMethodSelected,
	paymentPage,
	order,
	onCompletePayment,
	isPaymentAvailable,
	isPaymentPending,
	isAwaitingGatewayPayload,
	orderIsRefetching,
	openFrameInNewTab
}) => {
	const [selectedPage, setSelectedPage] = useState<PaymentPage>(null);
	const page = selectedPage ?? paymentPage ?? null;
	const [isRedirecting, setIsRedirecting] = useState(false);
	const { PaymentForm: css } = useTheme('PaymentForm');
	const { PriceParts: PricePartsOverride = PriceParts } = useRenderers();

	const { t, i18n } = useTranslation('PaymentForm');
	const { t: sentryT } = useSentryTranslation('PaymentForm');

	const {
		global: {
			companyInfo: { iataCode }
		}
	} = useConfig();

	const locale = getLocale();
	const testPaymentEnabled = process.env.TEST_PAYMENT_ENABLED === 'true';

	const isSingleMethod =
		!onTestPayment &&
		!!getSinglePaymentPage(
			{
				card,
				credit,
				gpay,
				kaspi,
				itransfer,
				fps,
				sbol,
				cash,
				invoice,
				qiwi,
				podeli,
				ffpBonus,
				uni_qr
			},
			{ includeCash: testPaymentEnabled }
		);
	const isPreOpened = !!paymentPage;
	const canAutoOpenSelectedMethod =
		!!page &&
		isPaymentMethodReadyToOpen(
			page.type,
			{
				card,
				credit,
				gpay,
				itransfer,
				kaspi,
				fps,
				sbol,
				cash,
				invoice,
				qiwi,
				podeli,
				ffpBonus,
				uni_qr
			},
			testPaymentEnabled
		);
	const hasPaymentContent = !!selectedPage?.content;
	const isWaitingToOpenMethod = !selectedPage && canAutoOpenSelectedMethod;
	const showPaymentLoader =
		isAwaitingGatewayPayload ||
		isWaitingToOpenMethod ||
		(!hasPaymentContent && (orderIsRefetching || isRedirecting || isPaymentPending));
	const resetPage = () => location.reload();

	const failModalOpen = !page && !isPaymentPending && isPaymentAvailable === false;

	const { total, ...priceToPayWithoutTotal } = priceToPay;
	const currency = total?.currency as Currency;

	useEffect(() => {
		onPaymentMethodSelected(page);
	}, [page, paymentPage]);

	const renderEmptyPaymentNotice = () => (
		<Notification
			infoMessage={{
				text: t(
					'Payment by this method is temporarily unavailable, please try refreshing the page or try again later.'
				),
				type: InfoMessageTypeEnum.Info,
				code: null
			}}
			status={InfoMessageTypeEnum.Info}
			type="context"
		/>
	);

	const onStartRedirecting = useCallback(() => setIsRedirecting(true), []);

	const getGatewayTranslation = useCallback(
		(method: CardPaymentMethodFragment) => {
			// [WEBSKY-4989]: display standard translations if the currency is different from RUB
			if (iataCode === '2S' && currency !== Currency.RUB) {
				return { title: null, header: null };
			}

			return {
				title: sentryT(`Title_${method.paymentProvider}`),
				header: sentryT(`Header_${method.paymentProvider}`)
			};
		},
		[iataCode, currency, sentryT]
	);

	const renderGatewayCards = useCallback(
		(method: CardPaymentMethodFragment, paymentType: PaymentType) => {
			if (
				paymentType === PaymentType.Card ||
				// [WEBSKY-4989]: display common cards if the currency is different from RUB
				(iataCode === '2S' && currency !== Currency.RUB)
			) {
				return (
					<>
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/sbp.svg"
							alt="SBP"
							className={cn(css.card, css.sbp)}
						/>
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/sberpay.svg"
							alt="SberPay"
							className={cn(css.card, css.sberpay)}
						/>
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/mastercard.svg"
							alt="Mastercard"
							className={cn(css.card, css.mastercard)}
						/>
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/visa.svg"
							alt="VISA"
							className={cn(css.card, css.visa)}
						/>
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/mir.svg"
							alt="МИР"
							className={cn(css.card, css.mir)}
						/>
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/apple-pay.svg"
							alt="ApplePay"
							className={cn(css.card, css.applepay)}
						/>
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/google-pay-1.svg"
							alt="GooglePay"
							className={cn(css.card, css.googlepay)}
						/>
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/yandex-pay.svg"
							alt="YandexPay"
							className={cn(css.card, css.yandexpay)}
						/>
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/vakifbank.svg"
							alt="VakifBank"
							className={cn(css.card, css.vakifbank)}
						/>
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/paylate.svg"
							alt="PaylateAsCard"
							className={cn(css.card, css.paylate_asCard)}
						/>
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/unionPay.svg"
							alt="UnionPay"
							className={cn(css.card, css.unionpay)}
						/>
					</>
				);
			}

			switch (method.paymentProvider) {
				case PaymentProvider.Idram:
					return (
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/idram.svg"
							alt="Idram"
							className={cn(css.idram, css.visible)}
						/>
					);
				case PaymentProvider.PayLate:
					return (
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/paylate.svg"
							alt="Paylate"
							className={cn(css.card, css.paylate, css.visible)}
						/>
					);
				case PaymentProvider.MoneyCarePro:
					return (
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/moneyCare.svg"
							alt="Paylate"
							className={cn(css.paylate, css.visible)}
						/>
					);
				case PaymentProvider.YandexPay:
					return (
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/yandex-pay.svg"
							alt="YandexPay"
							className={cn(css.card, css.yandexpay, css.visible)}
						/>
					);
				default:
					return <>{emptyCard}</>;
			}
		},
		[iataCode, currency]
	);

	return (
		<>
			{isRedirecting && (
				<div className={css.loader}>
					<PromoLoader text={t('PaymentInProgress')} />
				</div>
			)}

			<div
				className={cn(css.wrapper, {
					[css.oneMethod]: isSingleMethod,
					[css.hidden]: isRedirecting
				})}
				data-gatewaytype={card?.gatewayType}
				data-test-id="payment-form"
			>
				<PaymentResult
					result="fail"
					onClick={resetPage}
					onBackToOrder={onBackToOrder}
					isOpen={failModalOpen}
					onClose={onBackToOrder}
				/>

				<Summary className={css.details}>
					{order && (
						<SummaryGroup className={css.summaryGroup_order}>
							<Segments order={order} />
						</SummaryGroup>
					)}

					<SummaryGroup className={css.summaryGroup_priceParts}>
						<PricePartsOverride prices={priceToPayWithoutTotal as OrderPriceFragment} />
					</SummaryGroup>

					<SummaryGroup className={css.summaryGroup_total}>
						<SummaryItem
							className={css.details__totalItem}
							price={
								<h2 className={css.details__total}>
									{t('Total')}: <Money money={total} />
								</h2>
							}
						>
							{!!onBackToOrder && (
								<BaseButton
									className={css.details__button}
									variant="text"
									onClick={onBackToOrder}
									disabled={isPaymentPending}
								>
									{t('Edit order')}
								</BaseButton>
							)}
						</SummaryItem>
					</SummaryGroup>
				</Summary>
				<div className={css.content}>
					<h3 className={css.content__title}>
						<MediaQuery maxWidth={'mobile'}>
							{page && !isSingleMethod && (
								<Link className={css.content__title__back} action={resetPage}>
									{arrowIcon}
								</Link>
							)}
						</MediaQuery>

						<span>{page ? page.title : t('Choose a convenient payment method')}</span>

						<MediaQuery minWidth={'mobile'}>
							{page && !isSingleMethod && (
								<BaseButton variant="text" className={css.content__title__back} onClick={resetPage}>
									{t('Other methods')}
								</BaseButton>
							)}
						</MediaQuery>
					</h3>

					<div className={cn(css.content__tooltip)}>
						<Tooltip
							title={<div className={cn(css.content__tooltip__text)}>{t('Payment problems hint')}</div>}
							tooltipClassName={cn(css.tooltip)}
							enterTouchDelay={0}
						>
							<span>{t('Problems with payment?')}</span>
						</Tooltip>
					</div>

					{page && page.description && <p className={css.description}>{page.description}</p>}

					{order?.timelimit && (
						<PaymentTimeLimit
							variant="normal"
							className={css.timelimit}
							orderIsBooked={true}
							date={new Date(order.timelimit)}
							orderId={order.id}
						/>
					)}
					<ul
						className={cn(css.gateways, {
							[css.gateways_hidden]: !!page
						})}
					>
						<EmptyWrapper emptyRender={renderEmptyPaymentNotice} isRefetching={orderIsRefetching}>
							{gpay && (
								<GooglePay
									price={priceToPay?.total}
									onClick={setSelectedPage}
									onSuccess={onSuccess}
									onFail={onFail}
									isPreOpened={isPreOpened && page?.type === PaymentType.Gpay}
								/>
							)}

							{card && (
								<Iframe
									openFrameInNewTab={openFrameInNewTab}
									type={PaymentType.Card}
									frameInfo={card}
									onClick={setSelectedPage}
									isPreOpened={isPreOpened && page?.type === PaymentType.Card}
									onPaymentFrameLoaded={onPaymentFrameLoaded}
									gatewayTitle={getGatewayTranslation(card).title}
									headerTitle={getGatewayTranslation(card).header}
									children={
										<div className={css.cards}>{renderGatewayCards(card, PaymentType.Card)}</div>
									}
								/>
							)}

							{kaspi && (
								<Kaspi
									isPreOpened={isPreOpened && page?.type === PaymentType.Kaspi}
									isAwaitingGatewayPayload={isAwaitingGatewayPayload}
									frameInfo={kaspi}
									headerTitle={t('Kaspi')}
									onClick={setSelectedPage}
								>
									<div className={css.cards}>
										<img
											src="https://cdn.websky.aero/content/frontend/images/payment/kaspi.svg"
											alt={t('Kaspi')}
											className={cn(css.card, css.kaspi)}
										/>
									</div>
								</Kaspi>
							)}

							{credit && (
								<Iframe
									openFrameInNewTab={openFrameInNewTab}
									type={PaymentType.Credit}
									frameInfo={credit}
									onClick={setSelectedPage}
									isPreOpened={isPreOpened && page?.type === PaymentType.Credit}
									gatewayTitle={getGatewayTranslation(credit).title}
									headerTitle={getGatewayTranslation(credit).header}
									children={
										<div className={css.cards}>
											{renderGatewayCards(credit, PaymentType.Credit)}
										</div>
									}
								/>
							)}

							{itransfer && (
								<Iframe
									openFrameInNewTab={openFrameInNewTab}
									type={PaymentType.Itransfer}
									isPreOpened={isPreOpened && page?.type === PaymentType.Itransfer}
									frameInfo={itransfer}
									onClick={setSelectedPage}
									headerTitle={t('Invoicebox')}
									gatewayTitle={t('Invoicebox')}
									variant="page"
								>
									<div className={css.cards}>
										<img
											src={`https://cdn.websky.aero/content/frontend/images/payment/invoicebox${
												locale === 'ru' ? '-ru' : ''
											}.svg`}
											alt={t('Invoicebox')}
											className={cn(css.card, css.invoicebox)}
										/>
									</div>
								</Iframe>
							)}

							{sbol && (
								<Iframe
									type={PaymentType.Sbol}
									isPreOpened={isPreOpened && page?.type === PaymentType.Sbol}
									frameInfo={sbol}
									onClick={setSelectedPage}
									headerTitle={t('SberPay')}
									gatewayTitle={t('SberPay')}
									variant="page"
									onStartRedirecting={onStartRedirecting}
								>
									<div className={css.cards}>
										<img
											src="https://cdn.websky.aero/content/frontend/images/payment/sberpay.svg"
											alt={t('SberPay')}
											className={cn(css.icon)}
										/>
									</div>
								</Iframe>
							)}

							{fps && (
								<Iframe
									type={PaymentType.Fps}
									isPreOpened={isPreOpened && page?.type === PaymentType.Fps}
									frameInfo={fps}
									onClick={setSelectedPage}
									headerTitle={t('FPS')}
									gatewayTitle={t('FPS')}
									variant="page"
									onStartRedirecting={onStartRedirecting}
								>
									<div className={css.cards}>
										<img
											src="https://cdn.websky.aero/content/frontend/images/payment/sbp.svg"
											alt={t('FPS')}
											className={cn(css.icon)}
										/>

										{/* WEBSKY-8718: по дефолту скрыт стилями */}
										<img
											src="https://cdn.websky.aero/content/frontend/images/payment/uni-qr.svg"
											alt={t('UniQr')}
											className={css.uniQr}
										/>
									</div>
								</Iframe>
							)}

							{cash && testPaymentEnabled && (
								<Cash
									isPreOpened={isPreOpened && page?.type === PaymentType.Cash}
									frameInfo={cash}
									headerTitle={t('Cash')}
									type={PaymentType.Cash}
									onClick={setSelectedPage}
									onCompletePayment={onCompletePayment}
								/>
							)}

							{invoice && (
								<Invoice
									isPreOpened={isPreOpened && page?.type === PaymentType.Invoice}
									frameInfo={invoice}
									headerTitle={t('Invoice')}
									type={PaymentType.Invoice}
									onClick={setSelectedPage}
									onCompletePayment={onCompletePayment}
								/>
							)}

							{qiwi && (
								<Qiwi
									isPreOpened={isPreOpened && page?.type === PaymentType.Qiwi}
									isAwaitingGatewayPayload={isAwaitingGatewayPayload}
									frameInfo={qiwi}
									headerTitle={t('QIWI')}
									onClick={setSelectedPage}
								>
									<div className={css.cards}>
										<img
											src="https://cdn.websky.aero/content/frontend/images/payment/qiwi.svg"
											alt={t('QIWI')}
											className={css.qiwi}
										/>
									</div>
								</Qiwi>
							)}

							{podeli && (
								<Iframe
									variant="iframe"
									openFrameInNewTab={openFrameInNewTab}
									type={PaymentType.Podeli}
									frameInfo={podeli}
									onClick={setSelectedPage}
									isPreOpened={isPreOpened && page?.type === PaymentType.Podeli}
									onPaymentFrameLoaded={onPaymentFrameLoaded}
									gatewayTitle={t('Podeli')}
									headerTitle={t('Podeli')}
									children={
										<div className={css.cards}>
											<img
												src="https://cdn.websky.aero/content/frontend/images/payment/podeli.svg"
												alt={t('Podeli')}
												className={css.podeli}
											/>
										</div>
									}
								/>
							)}

							{ffpBonus && (
								<Iframe
									variant="iframe"
									openFrameInNewTab={openFrameInNewTab}
									type={PaymentType.FFPBonus}
									frameInfo={ffpBonus}
									onClick={setSelectedPage}
									isPreOpened={isPreOpened && page?.type === PaymentType.FFPBonus}
									onPaymentFrameLoaded={onPaymentFrameLoaded}
									gatewayTitle={t('FfpBonus')}
									headerTitle={t('FfpBonus')}
								>
									<div className={css.cards}>
										<img
											src="https://cdn.websky.aero/content/frontend/images/payment/ffp-bonus.svg"
											alt={t('FfpBonus')}
											className={css.ffpBonus}
										/>
									</div>
								</Iframe>
							)}

							{uni_qr && (
								<Iframe
									variant="iframe"
									openFrameInNewTab={openFrameInNewTab}
									type={PaymentType.UniQr}
									frameInfo={uni_qr}
									onClick={setSelectedPage}
									isPreOpened={isPreOpened && page?.type === PaymentType.UniQr}
									onPaymentFrameLoaded={onPaymentFrameLoaded}
									gatewayTitle={t('UniQr')}
									headerTitle={t('UniQr')}
								>
									<div className={css.cards}>
										<img
											src="https://cdn.websky.aero/content/frontend/images/payment/uni-qr.svg"
											alt={t('UniQr')}
											className={css.uniQr}
										/>
									</div>
								</Iframe>
							)}

							{onTestPayment && <Test onClick={onTestPayment} />}
						</EmptyWrapper>

						{i18n.exists('PaymentForm:Bottom notification') && (
							<SafeHtml
								tag="div"
								className={css.bottom_notification}
								html={t('PaymentForm:Bottom notification')}
							/>
						)}
					</ul>

					{page && (
						<div>
							{showPaymentLoader ? (
								<SimpleLoader />
							) : (
								<EmptyWrapper
									emptyRender={renderEmptyPaymentNotice}
									isRefetching={orderIsRefetching || isRedirecting}
								>
									{selectedPage?.content}
								</EmptyWrapper>
							)}
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default PaymentForm;
